import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from '../i18n/LanguageContext'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'

const GEMINI_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent'
const GEMINI_FALLBACK = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash:generateContent'
const MAX_CHARS  = 1000

const PLAN_LIMITS = {
  trial:    0,
  starter:  0,
  growth:   50,
  pro:      200,
  business: 500,
}

const PLAN_MAX_TOKENS = {
  growth:   400,
  pro:      700,
  business: 1000,
}

const SUGGESTIONS = {
  en: [
    'Give me a full sales summary for this month',
    'Which products should I restock urgently?',
    'Who are my most valuable customers and why?',
    'Compare this month vs last month performance',
    'What can I do to increase sales this week?',
    'Are there any patterns in my return orders?',
  ],
  bn: [
    'এই মাসের পুরো সেলস সামারি দাও',
    'কোন প্রোডাক্টগুলো এখনই restock করতে হবে?',
    'আমার সেরা কাস্টমারগুলো কারা এবং কেন?',
    'এই মাস বনাম গত মাসের performance তুলনা করো',
    'এই সপ্তাহে সেল বাড়াতে কী করতে পারি?',
    'আমার return order-এ কোনো pattern আছে কি?',
  ]
}

const GREETINGS = {
  en: `Hi! I'm Tara ✦

Your AI Business Agent at PageConnect. I have full access to your business data — sales, inventory, customers, goals, and more.

Ask me anything about your business, or request a detailed analysis.`,
  bn: `হ্যালো! আমি Tara ✦

তোমার PageConnect AI Business Agent। আমার কাছে তোমার পুরো বিজনেসের ডেটা আছে — সেল, স্টক, কাস্টমার, গোল সব কিছু।

বিজনেসের যেকোনো বিষয়ে জিজ্ঞেস করো বা বিস্তারিত analysis চাও।`,
}

function TaraStarIcon({ size = 18, color = '#2DD4B0' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z" fill={color}/>
      <path d="M19 3C19 3 19.4 5.2 20.2 6C21 6.8 23 7 23 7C23 7 21 7.2 20.2 8C19.4 8.8 19 11 19 11C19 11 18.6 8.8 17.8 8C17 7.2 15 7 15 7C15 7 17 6.8 17.8 6C18.6 5.2 19 3 19 3Z" fill={color} opacity="0.7"/>
    </svg>
  )
}

function TypingDots() {
  return (
    <div style={{ display:'flex', gap:'5px', padding:'4px 0' }}>
      {[0,1,2].map(i=>(
        <div key={i} style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#2DD4B0', animation:`taraTyping 1.2s ease-in-out ${i*0.2}s infinite` }}/>
      ))}
    </div>
  )
}

export default function TaraPage() {
  const { seller, getSubscriptionPlan } = useAuth()
  const { lang } = useTranslation()

  const plan      = getSubscriptionPlan?.() || 'trial'
  const limit     = PLAN_LIMITS[plan] || 0
  const maxTokens = PLAN_MAX_TOKENS[plan] || 400
  const allowed   = limit > 0

  const [messages,    setMessages]    = useState([])
  const [input,       setInput]       = useState('')
  const [loading,     setLoading]     = useState(false)
  const [queriesUsed, setQueriesUsed] = useState(0)
  const [context,     setContext]     = useState(null)
  const [contextReady, setContextReady] = useState(false)

  const messagesEndRef = useRef()
  const textareaRef    = useRef()

  const remaining  = Math.max(0, limit - queriesUsed)
  const canAsk     = allowed && remaining > 0
  const hasMessages = messages.length > 0

  useEffect(() => {
    if (!seller?.id) return
    fetchQueryCount()
    fetchContext()
    setMessages([{ role:'assistant', text:GREETINGS[lang]||GREETINGS.en, time:new Date() }])
  // eslint-disable-next-line
  }, [seller?.id, lang])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior:'smooth' })
  }, [messages, loading])

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px'
    }
  }, [input])

  const fetchQueryCount = async () => {
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
    const { count }  = await supabase.from('tara_queries').select('*',{count:'exact',head:true}).eq('seller_id',seller.id).gte('created_at',monthStart)
    setQueriesUsed(count||0)
  }

  const fetchContext = async () => {
    if (!seller?.id) return
    try {
      const now            = new Date()
      const monthStart     = new Date(now.getFullYear(),now.getMonth(),1).toISOString()
      const lastMonthStart = new Date(now.getFullYear(),now.getMonth()-1,1).toISOString()
      const lastMonthEnd   = new Date(now.getFullYear(),now.getMonth(),0,23,59,59).toISOString()
      const weekStart      = new Date(Date.now()-7*86400000).toISOString()
      const yearStart      = new Date(now.getFullYear(),0,1).toISOString()

      const [ordersRes,productsRes,customersRes,variantsRes,monthRes,lastMonthRes,weekRes,yearRes,invoicesRes,eventsRes,goalsRes,boostsRes] = await Promise.all([
        supabase.from('orders').select('id,order_code,status,total_amount,product_name,product_code,variant_name,quantity,unit_price,discount_amount,delivery_charge,payment_status,payment_method,order_source,branch_name,notes,created_at,customers(name,phone,district,division)').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(100),
        supabase.from('products').select('id,name,stock,sell_price,buy_price,product_code,category,product_type,color,season,status').eq('seller_id',seller.id).order('name').limit(200),
        supabase.from('customers').select('id,name,phone,district,division,facebook_url,created_at').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(100),
        supabase.from('product_variants').select('id,product_id,variant_name,stock,sell_price').eq('seller_id',seller.id).limit(200),
        supabase.from('orders').select('total_amount,status,product_name,payment_status').eq('seller_id',seller.id).gte('created_at',monthStart),
        supabase.from('orders').select('total_amount,status,product_name').eq('seller_id',seller.id).gte('created_at',lastMonthStart).lte('created_at',lastMonthEnd),
        supabase.from('orders').select('total_amount,status').eq('seller_id',seller.id).gte('created_at',weekStart),
        supabase.from('orders').select('total_amount,status').eq('seller_id',seller.id).gte('created_at',yearStart),
        supabase.from('seller_invoices').select('id,invoice_number,total_amount,status,client_name,created_at').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(20),
        supabase.from('seller_events').select('id,name,start_date,end_date,location,status,stall_fee,transport_cost').eq('seller_id',seller.id).order('start_date',{ascending:false}).limit(10),
        supabase.from('seller_goals').select('id,month,year,revenue_goal,order_goal,notes').eq('seller_id',seller.id).order('year',{ascending:false}).limit(6),
        supabase.from('boosts').select('id,fb_campaign_name,status,budget_bdt,goal,launched_at,ends_at,result_reach,result_clicks').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(10),
      ])

      const orders      = ordersRes.data      || []
      const products    = productsRes.data    || []
      const customers   = customersRes.data   || []
      const variants    = variantsRes.data    || []
      const monthOrds   = monthRes.data       || []
      const lastMonOrds = lastMonthRes.data   || []
      const weekOrds    = weekRes.data        || []
      const yearOrds    = yearRes.data        || []
      const invoices    = invoicesRes.data    || []
      const events      = eventsRes.data      || []
      const goals       = goalsRes.data       || []
      const boosts      = boostsRes.data      || []

      const monthRev   = monthOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
      const lastMonRev = lastMonOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
      const weekRev    = weekOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
      const yearRev    = yearOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)

      const statusCount = {}
      orders.forEach(o=>{ statusCount[o.status]=(statusCount[o.status]||0)+1 })
      const pending   = (statusCount.pending||0)+(statusCount.received||0)
      const confirmed = statusCount.confirmed||0
      const shipped   = statusCount.shipped||0
      const closed    = statusCount.closed||0
      const returned  = statusCount.return||0

      const paidCount   = monthOrds.filter(o=>o.payment_status==='Paid in full').length
      const unpaidCount = monthOrds.filter(o=>o.payment_status==='Unpaid COD').length

      const prodCount = {}
      monthOrds.forEach(o=>{ if(o.product_name) prodCount[o.product_name]=(prodCount[o.product_name]||0)+1 })
      const topMonthProds = Object.entries(prodCount).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([n,c])=>`${n}(${c})`)

      const lastProdCount = {}
      lastMonOrds.forEach(o=>{ if(o.product_name) lastProdCount[o.product_name]=(lastProdCount[o.product_name]||0)+1 })
      const topLastProds = Object.entries(lastProdCount).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([n,c])=>`${n}(${c})`)

      const outOfStock = products.filter(p=>p.stock===0).map(p=>p.name)
      const lowStock   = products.filter(p=>p.stock>0&&p.stock<=5).map(p=>`${p.name}(${p.stock})`)

      const custOrders = {}
      orders.forEach(o=>{ if(o.customers?.name) custOrders[o.customers.name]=(custOrders[o.customers.name]||0)+1 })
      const topCustomers = Object.entries(custOrders).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([n,c])=>`${n}(${c} orders)`)

      const monthName   = now.toLocaleDateString('en',{month:'long'})
      const lastMonName = new Date(now.getFullYear(),now.getMonth()-1,1).toLocaleDateString('en',{month:'long'})
      const currentGoal = goals.find(g=>g.month===now.getMonth()+1&&g.year===now.getFullYear())
      const activeBoosts = boosts.filter(b=>b.status==='running')

      const sellerCountry = seller?.country||'BD'
      const isAU = sellerCountry==='AU'||sellerCountry==='Australia'
      const isUS = sellerCountry==='US'||sellerCountry==='USA'
      const isBD = !isAU&&!isUS

      const culturalContext = isBD ? `
BANGLADESH F-COMMERCE CONTEXT:
Language awareness: Always follow the seller's actual language — if they write in English respond in English, if Bengali respond in Bengali, if mixed respond mixed. Never assume Bengali just because seller is from BD.
Bengali tone: Use "তুমি/তোমার" (not "আপনি/আপনার") — friendly and warm.
Peak occasions: Eid ul Fitr (biggest), Eid ul Adha, Pahela Baishakh Apr 14, Pahela Falgun, Durga Puja, Winter Nov-Feb, Wedding season Oct-Jan.
Islamic calendar dates: always say "approximately" — exact dates depend on moon sighting. If already passed this year, give next year's approximate date.
F-commerce: Facebook Live dominant, COD 60-70% normal, bKash advance = trust signal.
Couriers: Pathao (Dhaka ৳60-80), Steadfast/RedX (outside ৳100-130).`
      : isAU ? `AUSTRALIA: Seasons opposite. GST 10%. PayID, Afterpay. Peak: Christmas, Boxing Day, EOFY.`
      : `USA: Credit card, PayPal. Peak: Black Friday, Cyber Monday, Christmas.`

      setContext(`=== SELLER PROFILE ===
Shop: ${seller.shop_name||'Unknown'} | Plan: ${plan}
Currency: ${seller.seller_currency||'BDT'} | Country: ${seller.country||'BD'}

=== REVENUE ===
${monthName} (this month): ৳${Math.round(monthRev).toLocaleString()} from ${monthOrds.length} orders
${lastMonName} (last month): ৳${Math.round(lastMonRev).toLocaleString()} from ${lastMonOrds.length} orders
This week: ৳${Math.round(weekRev).toLocaleString()} | This year: ৳${Math.round(yearRev).toLocaleString()}
Month-on-month: ${lastMonRev>0?((monthRev-lastMonRev)/lastMonRev*100).toFixed(1)+'%':'N/A'}

=== ORDERS ===
Total: ${orders.length} | ${monthName}: ${monthOrds.length} | Last month: ${lastMonOrds.length}
Status: Pending/Received=${pending}, Confirmed=${confirmed}, Shipped=${shipped}, Closed=${closed}, Returned=${returned}
Payment: Paid=${paidCount}, Unpaid COD=${unpaidCount}
Top products ${monthName}: ${topMonthProds.join(', ')||'No data'}
Top products ${lastMonName}: ${topLastProds.join(', ')||'No data'}

=== RECENT 15 ORDERS ===
${orders.slice(0,15).map(o=>`${o.order_code}|${o.customers?.name||'Unknown'}|${o.product_name||'N/A'}|${o.status}|৳${o.total_amount}|${o.payment_status}`).join('\n')}

=== PRODUCTS (${products.length} total) ===
Out of stock(${outOfStock.length}): ${outOfStock.slice(0,10).join(', ')||'None'}
Low stock ≤5(${lowStock.length}): ${lowStock.slice(0,10).join(', ')||'None'}
Products: ${products.slice(0,30).map(p=>`${p.name}|stock:${p.stock}|price:৳${p.sell_price}|${p.category||'N/A'}`).join(' / ')}
Variants: ${variants.slice(0,20).map(v=>`${v.variant_name}|stock:${v.stock}`).join(', ')}

=== CUSTOMERS (${customers.length} total) ===
Top buyers: ${topCustomers.join(', ')||'No data'}
Recent: ${customers.slice(0,10).map(c=>`${c.name}|${c.phone}|${c.district||'N/A'}`).join(' / ')}

=== INVOICES ===
${invoices.slice(0,5).map(i=>`${i.invoice_number}|${i.client_name}|৳${i.total_amount}|${i.status}`).join('\n')||'No invoices'}

=== EVENTS ===
${events.slice(0,3).map(e=>`${e.name}|${e.start_date}|${e.location||'N/A'}|${e.status}`).join('\n')||'No events'}

=== GOALS ===
${currentGoal?`${monthName}: Revenue goal=৳${currentGoal.revenue_goal?.toLocaleString()}, Orders goal=${currentGoal.order_goal}, Progress: Revenue ${((monthRev/currentGoal.revenue_goal)*100).toFixed(0)}%, Orders ${((monthOrds.length/currentGoal.order_goal)*100).toFixed(0)}%`:'No goal set'}

=== ADBOOST ===
Active: ${activeBoosts.length} | Total: ${boosts.length}
${activeBoosts.map(b=>`${b.fb_campaign_name}|${b.goal}|ends:${b.ends_at?.split('T')[0]}`).join(', ')||'None'}

${culturalContext}`)
      setContextReady(true)
    } catch(err) {
      console.error('Tara context error:', err)
      setContextReady(true)
    }
  }

  const buildSystemPrompt = useCallback((question) => {
    const recentHistory = messages.slice(-10)
    return `You are Tara, the AI Business Agent for ${seller?.shop_name||'this shop'} on PageConnect.
Today: ${new Date().toLocaleDateString('en',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}

PERSONALITY: Warm, smart, encouraging. Like a brilliant friend who truly knows your business. Conversational, direct, genuinely helpful. Use light emojis when it feels natural.

LANGUAGE RULE: ALWAYS respond in the exact same language the seller used. English = English. Bengali = Bengali. Mixed = mixed. Never switch.

SELLER BUSINESS DATA:
${context||'Loading...'}

ANSWER RULES:
- Use exact numbers from business data
- This is a FULL PAGE chat — you can give detailed, thorough answers. No need to keep it short.
- General world/business questions: answer confidently
- Sensitive topics: neutral
- Islamic calendar dates: always say "approximately" — depends on moon sighting
- Legal/tax: general guidance, recommend professional for specifics
- Never support illegal activities
- If data shows something concerning, proactively mention it

CONVERSATION HISTORY:
${recentHistory.map(m=>`${m.role==='user'?'Seller':'Tara'}: ${m.text}`).join('\n')}

Seller's question: ${question}

(Important business decisions should be verified with relevant professionals.)`
  }, [context, messages, seller?.shop_name])

  const ask = async (question) => {
    const q = question.trim()
    if (!q||loading||!canAsk) return

    setMessages(prev=>[...prev,{role:'user',text:q,time:new Date()}])
    setInput('')
    setLoading(true)

    try {
      const geminiBody = JSON.stringify({
  contents:[{parts:[{text:buildSystemPrompt(q)}]}],
  generationConfig:{temperature:0.8,maxOutputTokens:maxTokens},
})
let response = await fetch(`${GEMINI_URL}?key=${process.env.REACT_APP_GEMINI_KEY}`,{
  method:'POST',
  headers:{'Content-Type':'application/json'},
  body:geminiBody,
})
if(!response.ok){
  response = await fetch(`${GEMINI_FALLBACK}?key=${process.env.REACT_APP_GEMINI_KEY}`,{
    method:'POST',
    headers:{'Content-Type':'application/json'},
    body:geminiBody,
  })
}
if(!response.ok){const err=await response.json();throw new Error(err?.error?.message||'API error')}
const data   = await response.json()
      const answer = data?.candidates?.[0]?.content?.parts?.[0]?.text
      if(!answer) throw new Error('Empty response')

      setMessages(prev=>[...prev,{role:'assistant',text:answer.trim(),time:new Date()}])
      await supabase.from('tara_queries').insert({seller_id:seller.id,question:q.slice(0,500)})
      setQueriesUsed(prev=>prev+1)

    } catch(err) {
      setMessages(prev=>[...prev,{
        role:'assistant',
        text:lang==='bn'?'উফ! কিছু একটা ভুল হয়েছে । একটু পরে আবার চেষ্টা করো!':'Oops! Something went wrong. Try again in a moment!',
        time:new Date(),
        error:true,
      }])
    }
    setLoading(false)
  }

  const handleKey = (e) => {
    if (e.key==='Enter'&&!e.shiftKey&&!e.ctrlKey) { e.preventDefault(); ask(input) }
  }

  const formatTime = (date) => date?.toLocaleTimeString('en-BD',{hour:'2-digit',minute:'2-digit'})

  if (!allowed) {
    return (
      <div className="flex-1 pc-page" style={{ background:'var(--bg-page)', display:'flex', flexDirection:'column' }}>
        <Topbar title="Ask Tara"/>
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'40px 24px' }}>
          <div style={{ textAlign:'center', maxWidth:'400px' }}>
            <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', boxShadow:'0 4px 20px rgba(11,31,58,0.3)' }}>
              <TaraStarIcon size={32} color='#2DD4B0'/>
            </div>
            <h2 style={{ fontSize:'20px', fontWeight:800, color:'var(--text-primary)', marginBottom:'10px', fontFamily:'var(--font-ui)' }}>Tara is on Growth plan and above</h2>
            <p style={{ fontSize:'13px', color:'var(--text-muted)', lineHeight:1.7, marginBottom:'24px', fontFamily:'var(--font-body)' }}>
              Upgrade to Growth for {PLAN_LIMITS.growth} monthly queries, Pro for {PLAN_LIMITS.pro}, or Business for {PLAN_LIMITS.business} — with longer, more detailed responses.
            </p>
            <a href="/pricing" style={{ display:'inline-block', padding:'12px 28px', borderRadius:'var(--radius-md)', background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', color:'white', fontWeight:600, fontSize:'13px', textDecoration:'none', fontFamily:'var(--font-ui)', boxShadow:'0 4px 16px rgba(11,31,58,0.3)' }}>
              View plans
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 pc-page" style={{ background:'var(--bg-page)', display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden' }}>
      <Topbar title="Ask Tara"/>

      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>

        {/* ── Main chat area — Gemini style ── */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', maxWidth:'760px', margin:'0 auto', width:'100%', padding:'0 24px' }}>

          {/* Messages or empty state */}
          <div style={{ flex:1, overflowY:'auto', paddingTop:'32px', paddingBottom:'16px', scrollbarWidth:'thin', scrollbarColor:'var(--border) transparent' }}>

            {/* Empty state — centered greeting like Gemini */}
            {!hasMessages && (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', textAlign:'center' }}>
                <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'20px', boxShadow:'0 4px 20px rgba(11,31,58,0.25)' }}>
                  <TaraStarIcon size={32} color='#2DD4B0'/>
                </div>
                <h1 style={{ fontSize:'28px', fontWeight:800, color:'var(--text-primary)', fontFamily:'var(--font-ui)', marginBottom:'8px' }}>
                  {lang==='bn'?'হ্যালো! আমি Tara ✦':'Hi there! I\'m Tara ✦'}
                </h1>
                <p style={{ fontSize:'14px', color:'var(--text-muted)', maxWidth:'460px', lineHeight:1.7, fontFamily:'var(--font-body)', marginBottom:'32px' }}>
                  {lang==='bn'
                    ?`তোমার AI Business Agent। আমার কাছে তোমার পুরো বিজনেসের ডেটা আছে। যেকোনো কিছু জিজ্ঞেস করো।`
                    :`Your AI Business Agent. I have access to all your business data — sales, inventory, customers, goals. Ask me anything.`}
                </p>

                {/* Suggestion chips — Gemini style grid */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', width:'100%', maxWidth:'560px' }}>
                  {(SUGGESTIONS[lang]||SUGGESTIONS.en).map((s,i)=>(
                    <button key={i} onClick={()=>ask(s)}
                      style={{ padding:'14px 16px', borderRadius:'var(--radius-lg)', background:'var(--bg-card)', border:'1px solid var(--border)', fontSize:'13px', color:'var(--text-primary)', cursor:'pointer', textAlign:'left', fontFamily:'var(--font-body)', lineHeight:1.4, boxShadow:'var(--shadow-sm)', transition:'var(--transition-base)' }}
                      onMouseEnter={e=>{ e.currentTarget.style.borderColor='var(--border-focus)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; e.currentTarget.style.transform='translateY(-1px)' }}
                      onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='var(--shadow-sm)'; e.currentTarget.style.transform='translateY(0)' }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {hasMessages && (
              <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
                {messages.map((msg,i)=>{
                  const isUser = msg.role==='user'
                  return (
                    <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:isUser?'flex-end':'flex-start', gap:'6px' }}>
                      {/* Label */}
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', paddingLeft:isUser?0:'4px', paddingRight:isUser?'4px':0 }}>
                        {!isUser && (
                          <div style={{ width:'24px', height:'24px', borderRadius:'50%', background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <TaraStarIcon size={11} color='#2DD4B0'/>
                          </div>
                        )}
                        <span style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-ui)' }}>
                          {isUser?'You':'Tara'}
                        </span>
                        <span style={{ fontSize:'10.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{formatTime(msg.time)}</span>
                      </div>

                      {/* Bubble */}
                      <div style={{ maxWidth:isUser?'75%':'100%', padding:isUser?'10px 16px':'12px 0', borderRadius:isUser?'var(--radius-lg)':'0', background:isUser?'linear-gradient(135deg,#0B1F3A,#1a3a6e)':'transparent', color:isUser?'white':'var(--text-primary)', fontSize:'14px', lineHeight:1.7, whiteSpace:'pre-wrap', fontFamily:'var(--font-body)', boxShadow:isUser?'0 2px 8px rgba(11,31,58,0.2)':'none' }}>
                        {msg.text}
                      </div>
                    </div>
                  )
                })}

                {loading && (
                  <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', paddingLeft:'4px' }}>
                      <div style={{ width:'24px', height:'24px', borderRadius:'50%', background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <TaraStarIcon size={11} color='#2DD4B0'/>
                      </div>
                      <span style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-ui)' }}>Tara</span>
                    </div>
                    <div style={{ paddingLeft:'4px' }}><TypingDots/></div>
                  </div>
                )}
              </div>
            )}

            <div ref={messagesEndRef}/>
          </div>

          {/* ── Input area — Gemini style bottom bar ── */}
          <div style={{ flexShrink:0, paddingBottom:'20px' }}>
            {!canAsk ? (
              <div style={{ padding:'14px 20px', background:'var(--error-light)', border:'1px solid var(--error-border)', borderRadius:'var(--radius-lg)', textAlign:'center', fontSize:'13px', color:'var(--error)', fontFamily:'var(--font-body)' }}>
                {lang==='bn'?'এই মাসের limit শেষ। পরের মাসের ১ তারিখে reset হবে।':'Monthly query limit reached. Resets on the 1st of next month.'}
              </div>
            ) : (
              <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-xl)', boxShadow:'var(--shadow-lg)', overflow:'hidden', transition:'border-color 0.15s, box-shadow 0.15s' }}
                onFocusCapture={e=>{ e.currentTarget.style.borderColor='var(--border-focus)'; e.currentTarget.style.boxShadow='0 0 0 3px var(--primary-light), var(--shadow-lg)' }}
                onBlurCapture={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='var(--shadow-lg)' }}>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={e=>{ if(e.target.value.length<=MAX_CHARS) setInput(e.target.value) }}
                  onKeyDown={handleKey}
                  placeholder={lang==='bn'?'Tara কে জিজ্ঞেস করো... (Enter to send, Shift+Enter for new line)':'Ask Tara anything... (Enter to send, Shift+Enter for new line)'}
                  disabled={loading}
                  style={{ width:'100%', border:'none', outline:'none', padding:'16px 20px 0', fontSize:'14px', color:'var(--text-primary)', background:'transparent', fontFamily:'var(--font-body)', resize:'none', lineHeight:1.6, minHeight:'56px', maxHeight:'160px', boxSizing:'border-box', display:'block' }}
                />
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px 12px 20px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                    <span style={{ fontSize:'11.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
                      {remaining} of {limit} queries left
                    </span>
                    {!contextReady && (
                      <span style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>Loading data...</span>
                    )}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    {input.length>MAX_CHARS*0.8&&(
                      <span style={{ fontSize:'11px', color:input.length>MAX_CHARS?'var(--error)':'var(--text-muted)', fontFamily:'var(--font-body)' }}>{MAX_CHARS-input.length}</span>
                    )}
                    <button onClick={()=>ask(input)} disabled={!input.trim()||loading||!contextReady}
                      style={{ width:'38px', height:'38px', borderRadius:'var(--radius-md)', border:'none', background:input.trim()&&!loading&&contextReady?'linear-gradient(135deg,#0B1F3A,#1a3a6e)':'var(--bg-muted)', cursor:input.trim()&&!loading&&contextReady?'pointer':'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', transition:'var(--transition-base)' }}>
                      {loading
                        ? <div style={{ width:'15px', height:'15px', borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#2DD4B0', animation:'spin 0.6s linear infinite' }}/>
                        : <svg width="16" height="16" viewBox="0 0 20 20" fill={input.trim()&&contextReady?'white':'var(--text-muted)'}><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                      }
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div style={{ textAlign:'center', marginTop:'8px' }}>
              <span style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>Tara by PageConnect · Powered by Gemini · {plan.charAt(0).toUpperCase()+plan.slice(1)} plan</span>
{hasMessages && (
  <div style={{ display:'flex', gap:'8px', justifyContent:'center', flexWrap:'wrap', marginTop:'10px' }}>
    {(SUGGESTIONS[lang]||SUGGESTIONS.en).slice(0,3).map((s,i)=>(
      <button key={i} onClick={()=>ask(s)}
        style={{ fontSize:'11.5px', color:'var(--text-muted)', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'20px', padding:'5px 12px', cursor:'pointer', fontFamily:'var(--font-body)', transition:'var(--transition-base)' }}
        onMouseEnter={e=>e.currentTarget.style.borderColor='var(--border-focus)'}
        onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
        {s}
      </button>
    ))}
  </div>
)}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes taraTyping {
          0%,60%,100% { transform:translateY(0); opacity:0.4 }
          30%           { transform:translateY(-5px); opacity:1 }
        }
        @keyframes spin { to { transform:rotate(360deg) } }
      `}</style>
    </div>
  )
}